import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './tableList.css';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Checkbox,
    FormControlLabel,
    FormControl,
    Select,
    MenuItem,
    Pagination,
    Snackbar,
    Alert,
    IconButton,
    Menu,
    Switch,
} from '@mui/material';
import { FilterList, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import PaginationItem from '@mui/material/PaginationItem';
import book1 from '../../Images/book1.jpg';
import DateRangeFilter from '../Filters/DateRangeFilter';
import { categoryAPI, productAPI } from '../../services/apiService';

const convertPersianToEnglishDigits = (str) => {
    if (str === null || str === undefined) return '';
    return String(str).replace(/[۰-۹]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d));
};

const filterNumericInput = (str) => str.replace(/[^0-9۰-۹]/g, '');

const convertEnglishToPersianDigits = (str) => {
    if (str === null || str === undefined) return '';
    return String(str).replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d]);
};

const handleNumericKeyDown = (e) => {
    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];

    if (!allowedKeys.includes(e.key) && !e.key.match(/[0-9۰-۹]/)) {
        e.preventDefault();
    }
};

function TableList() {
    const { t, i18n } = useTranslation();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [itemTypes, setItemTypes] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [filters, setFilters] = useState({
        priceOrder: null,
        category: '',
        dateFrom: '',
        dateTo: '',
        status: 'all',
    });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const formatNumberByLanguage = (num) => {
        const locale = i18n.language === 'fa' ? 'fa-IR' : 'en-US';
        return new Intl.NumberFormat(locale).format(num);
    };

    const location = useLocation();
    const itemsPerPage = 10;

    useEffect(() => {
        if (location.state?.successMessage) {
            setSnackbar({
                open: true,
                message: location.state.successMessage,
                severity: 'success',
            });
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    const handleSnackbarClose = () => setSnackbar((prev) => ({ ...prev, open: false }));

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const result = await productAPI.getAll();
                setData(result || []);
            } catch (err) {
                setError(err.message || t('error'));
            } finally {
                setLoading(false);
            }
        };
        fetchProducts();
    }, [t]);

    useEffect(() => {
        const fetchItemTypes = async () => {
            try {
                const res = await categoryAPI.getAll();
                setItemTypes(res || []);
            } catch (err) {
                console.error('Error fetching item types:', err);
            }
        };
        fetchItemTypes();
    }, [t]);

    const getItemTypeName = (id) => {
        const found = itemTypes.find((type) => type.id === id);
        return found ? found.title : id;
    };

    const handleChangePage = (event, value) => {
        setCurrentPage(value);
    };

    const confirmDelete = (id) => {
        setSelectedId(id);
        setOpenDialog(true);
    };

    const handleClose = () => {
        setOpenDialog(false);
        setSelectedId(null);
    };

    const handleConfirmDelete = async () => {
        if (!selectedId) return;
        try {
            await productAPI.remove(selectedId);
            setData((prev) => prev.filter((prod) => prod.id !== selectedId));
            setSnackbar({
                open: true,
                message: t('delete_success'),
                severity: 'success',
            });
        } catch (err) {
            console.error(err);
            setSnackbar({
                open: true,
                message: t('delete_failed'),
                severity: 'error',
            });
        } finally {
            handleClose();
        }
    };

    const handleEdit = (product) => {
        setEditingProduct({ ...product });
        setImagePreview(product.image || null);
        setOpenEditDialog(true);
    };

    const handleEditChange = (e) => {
        const { name, type, value, checked } = e.target;
        let newVal;
        if (type === 'checkbox') {
            newVal = !!checked;
        } else if (name === 'price' || name === 'priority') {
            const filtered = filterNumericInput(value);
            newVal = filtered;
        } else {
            newVal = value;
        }
        setEditingProduct((prev) => ({
            ...prev,
            [name]: newVal,
        }));
    };

    const handleSelectCategory = (e) => {
        const selected = e.target.value;
        setEditingProduct((prev) => ({
            ...prev,
            item_type: selected,
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        setEditingProduct((prev) => ({
            ...prev,
            newImage: file,
        }));
        const reader = new FileReader();
        reader.onloadend = () => setImagePreview(reader.result);
        reader.readAsDataURL(file);
    };

    const handleSaveEdit = async () => {
        if (!editingProduct) return;
        try {
            const { id } = editingProduct;
            const formData = new FormData();
            formData.append('title', editingProduct.title || '');
            formData.append('description', editingProduct.description || '');
            formData.append(
                'price',
                editingProduct.price ? convertPersianToEnglishDigits(editingProduct.price) : ''
            );
            formData.append('available', editingProduct.available ? 'true' : 'false');
            formData.append(
                'priority',
                editingProduct.priority
                    ? convertPersianToEnglishDigits(editingProduct.priority)
                    : ''
            );
            formData.append('item_type', editingProduct.item_type || '');
            if (editingProduct.newImage) formData.append('image', editingProduct.newImage);
            const updatedProduct = await productAPI.update(id, formData);
            setData((prev) =>
                prev.map((p) =>
                    p.id === id
                        ? { ...p, ...editingProduct, image: updatedProduct.image || p.image }
                        : p
                )
            );
            setOpenEditDialog(false);
            setEditingProduct(null);
            setImagePreview(null);
            setSnackbar({
                open: true,
                message: t('edit_success'),
                severity: 'success',
            });
        } catch (err) {
            console.error(err);
            setSnackbar({
                open: true,
                message: t('edit_failed'),
                severity: 'error',
            });
        }
    };

    let filteredData = data.filter((item) =>
        String(item.title || '')
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
    );

    if (filters.category)
        filteredData = filteredData.filter((i) => i.item_type === filters.category);

    if (filters.status === 'active') filteredData = filteredData.filter((i) => i.available);
    else if (filters.status === 'inactive') filteredData = filteredData.filter((i) => !i.available);

    if (filters.dateFrom || filters.dateTo) {
        filteredData = filteredData.filter((i) => {
            const updated = new Date(i.updated_date);
            const from = filters.dateFrom ? new Date(filters.dateFrom) : null;
            const to = filters.dateTo ? new Date(filters.dateTo) : null;
            if (Number.isNaN(updated)) return true;
            if (from && Number.isNaN(from)) return true;
            if (to && Number.isNaN(to)) return true;
            return (!from || updated >= from) && (!to || updated <= to);
        });
    }

    if (filters.priceOrder === 'asc')
        filteredData = [...filteredData].sort((a, b) => a.price - b.price);
    else if (filters.priceOrder === 'desc')
        filteredData = [...filteredData].sort((a, b) => b.price - a.price);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    if (loading) return <p>{t('loading')}</p>;
    if (error)
        return (
            <p>
                {t('error')}: {error}
            </p>
        );
    return (
        <div>
            <div className="table_list_header">
                <div className="search_bar">
                    <input
                        type="text"
                        placeholder={t('search_products')}
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="user_search_input"
                    />
                </div>
            </div>
            <TableContainer component={Paper} className="table_list">
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('product_image')}</TableCell>
                            <TableCell>{t('product_name')}</TableCell>
                            <TableCell>
                                {t('price')}
                                <IconButton
                                    size="small"
                                    onClick={() =>
                                        setFilters((prev) => {
                                            let nextOrder;
                                            if (prev.priceOrder === 'asc') nextOrder = 'desc';
                                            else if (prev.priceOrder === 'desc') nextOrder = null;
                                            else nextOrder = 'asc';
                                            return { ...prev, priceOrder: nextOrder };
                                        })
                                    }
                                >
                                    {(() => {
                                        if (filters.priceOrder === 'asc')
                                            return <ArrowUpward fontSize="small" />;
                                        if (filters.priceOrder === 'desc')
                                            return <ArrowDownward fontSize="small" />;
                                        return <FilterList fontSize="small" />;
                                    })()}
                                </IconButton>
                            </TableCell>
                            <TableCell>
                                {t('category')}
                                <CategoryFilter
                                    categories={itemTypes}
                                    value={filters.category}
                                    onChange={(val) =>
                                        setFilters((prev) => ({ ...prev, category: val }))
                                    }
                                />
                            </TableCell>
                            <TableCell>
                                {t('updated_date')}
                                {/* <DateRangeFilter
                                    value={{ from: filters.dateFrom, to: filters.dateTo }}
                                    onChange={(range) =>
                                        setFilters((prev) => ({
                                            ...prev,
                                            dateFrom: range.from,
                                            dateTo: range.to,
                                        }))
                                    }
                                /> */}
                            </TableCell>
                            <TableCell>{t('priority')}</TableCell>
                            <TableCell>
                                {t('status')}
                                <IconButton
                                    size="small"
                                    onClick={() =>
                                        setFilters((prev) => {
                                            let nextStatus;
                                            if (prev.status === 'all') nextStatus = 'inactive';
                                            else if (prev.status === 'inactive')
                                                nextStatus = 'active';
                                            else nextStatus = 'all';
                                            return { ...prev, status: nextStatus };
                                        })
                                    }
                                >
                                    <FilterList fontSize="small" />
                                </IconButton>
                            </TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {currentItems.map((row) => (
                            <TableRow key={row.id}>
                                <TableCell>
                                    <div className="product_idd">
                                        <img
                                            src={row.image || book1}
                                            alt="product"
                                            className="product_img"
                                        />
                                    </div>
                                </TableCell>
                                <TableCell className="product_idd_text">{row.title}</TableCell>
                                <TableCell className="product_idd_text">
                                    {i18n.language === 'fa'
                                        ? convertEnglishToPersianDigits(row.price)
                                        : convertPersianToEnglishDigits(row.price)}
                                    {t('toman')}
                                </TableCell>
                                <TableCell className="product_idd_text">
                                    {getItemTypeName(row.item_type)}
                                </TableCell>
                                <TableCell className="product_idd_text">
                                    {row.updated_date
                                        ? new Date(row.updated_date).toLocaleDateString(
                                              i18n.language === 'fa' ? 'fa-IR' : 'en-US'
                                          )
                                        : '-'}
                                </TableCell>
                                <TableCell className="product_idd_text">
                                    {i18n.language === 'fa'
                                        ? convertEnglishToPersianDigits(row.priority)
                                        : convertPersianToEnglishDigits(row.priority)}
                                </TableCell>
                                <TableCell className="product_idd_text">
                                    <span
                                        className={`status
                                            ${row.available ? 'active' : 'inactive'}`}
                                    >
                                        {row.available ? t('active') : t('inactive')}
                                    </span>
                                </TableCell>
                                <TableCell className="tabel_buttons">
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={() => confirmDelete(row.id)}
                                    >
                                        {t('delete')}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="success"
                                        onClick={() => handleEdit(row)}
                                    >
                                        {t('edit')}
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {totalPages > 1 && (
                    <div className="orders_pagination">
                        <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={handleChangePage}
                            color="primary"
                            variant="outlined"
                            hidePrevButton
                            hideNextButton
                            renderItem={(item) => (
                                <PaginationItem
                                    // eslint-disable-next-line react/jsx-props-no-spreading
                                    {...item}
                                    page={item.page ? formatNumberByLanguage(item.page) : item.page}
                                />
                            )}
                        />
                    </div>
                )}
            </TableContainer>
            <Dialog open={openDialog} onClose={handleClose}>
                <DialogTitle>{t('confirm_delete')}</DialogTitle>
                <DialogContent>
                    <DialogContentText> {t('delete_confirmation_message')} </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="primary">
                        {t('no')}
                    </Button>
                    <Button onClick={handleConfirmDelete} color="error">
                        {t('yes_delete')}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={openEditDialog}
                onClose={() => setOpenEditDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{t('edit_product')}</DialogTitle>
                <DialogContent>
                    {editingProduct && (
                        <>
                            <p className="input_lable">{t('product_name')}</p>
                            <TextField
                                margin="dense"
                                name="title"
                                className="popUp_input"
                                placeholder={t('product_name')}
                                value={editingProduct.title || ''}
                                onChange={handleEditChange}
                                fullWidth
                            />
                            <p className="input_lable">{t('price_toman')}</p>
                            <TextField
                                margin="dense"
                                name="price"
                                placeholder={t('price_toman')}
                                onKeyDown={handleNumericKeyDown}
                                value={
                                    editingProduct.price !== undefined ? editingProduct.price : ''
                                }
                                onChange={handleEditChange}
                                fullWidth
                            />
                            <p className="input_lable">{t('description')}</p>
                            <TextField
                                margin="dense"
                                className="popUp_input"
                                name="description"
                                placeholder={t('description')}
                                value={editingProduct.description || ''}
                                onChange={handleEditChange}
                                fullWidth
                                multiline
                            />
                            <p className="input_lable">{t('category')}</p>
                            <FormControl fullWidth margin="dense">
                                <Select
                                    className="popUp_input"
                                    labelId="cat-label"
                                    value={editingProduct.item_type || ''}
                                    onChange={handleSelectCategory}
                                >
                                    <MenuItem value="">{t('select')}</MenuItem>
                                    {itemTypes.map((c) => (
                                        <MenuItem key={c.id} value={c.id}>
                                            {c.title}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <p className="input_lable">{t('priority')}</p>
                            <TextField
                                margin="dense"
                                name="priority"
                                className="popUp_input"
                                placeholder={t('priority')}
                                onKeyDown={handleNumericKeyDown}
                                value={
                                    editingProduct.priority !== undefined
                                        ? editingProduct.priority
                                        : ''
                                }
                                onChange={handleEditChange}
                                fullWidth
                            />
                            <div style={{ marginTop: 12 }}>
                                <p style={{ margin: '6px 0' }}>{t('product_image')}</p>
                                {imagePreview && (
                                    <img
                                        src={imagePreview}
                                        alt="Preview"
                                        style={{ width: 120, display: 'block', marginBottom: 8 }}
                                    />
                                )}
                                <input type="file" accept="image/*" onChange={handleImageChange} />
                            </div>
                            <FormControlLabel
                                control={
                                    <Switch
                                        className="ios-switch"
                                        checked={!!editingProduct.available}
                                        onChange={(e) =>
                                            setEditingProduct((prev) => ({
                                                ...prev,
                                                available: e.target.checked,
                                            }))
                                        }
                                    />
                                }
                                label={t('product_availability')}
                                labelPlacement="start"
                                className="iosSwitchLabel"
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenEditDialog(false)}>{t('cancel')}</Button>
                    <Button onClick={handleSaveEdit} color="success">
                        {t('save_changes')}
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>
        </div>
    );
}

function CategoryFilter({ categories, value, onChange }) {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    return (
        <>
            <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
                <FilterList fontSize="small" />
            </IconButton>
            <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
                <MenuItem
                    selected={value === ''}
                    onClick={() => {
                        onChange('');
                        setAnchorEl(null);
                    }}
                >
                    {t('all')}
                </MenuItem>
                {categories.map((cat) => (
                    <MenuItem
                        key={cat.id}
                        selected={value === cat.id}
                        onClick={() => {
                            onChange(cat.id);
                            setAnchorEl(null);
                        }}
                    >
                        {cat.title}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

export default TableList;
